import _ from 'lodash';
import * as R from 'ramda';
import Vue from 'vue';
import { t } from '@/ui/i18n/i18n';
import { INSPECTION_ITEM_TYPE, INSPECTION_LIST_CONDITIONS, CHECKLIST_COLLAPSE_THRESHOLD } from '@/_legacy/constants';
import initialState from '@/_legacy/store/checklist_builder/state';
import encoders from '@/_legacy/store/checklist_builder/encoders';
import { v4 as uuidv4 } from 'uuid';
import { eventBus } from '@/_legacy/eventBus';
import { generateHeaderPage, getDefaultTitleSettings } from '@/_legacy/services/checklist';
import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import findLast from 'lodash/findLast';
import { deleteItem } from '@/_legacy/store/deleteItemFn';
import { ResponsesTypes } from '@/entities/checklist';
import { hasCollapsibleTemplateBuilder } from '@/ui/auth/auth';
import { INSPECTION_ITEM_RESPONSE } from '../../constants';
import InspectionEntityItemsHelper from '@/_legacy/helpers/inspectionEntityItemsHelper';
import { isWordTemplateReportEnabled } from '@/ui/helpers/featureHelper';

const findRootItem = (node, uuid, limitType = null) => {
  if (node.uuid === uuid) {
    return node;
  }

  if (node.items) {
    for (const item of node.items) {
      const root = findRootItem(item, uuid);

      if (root && root.type === limitType) {
        return root;
      }

      if (root) {
        return node;
      }
    }
  }

  return null;
};

const findFormItem = (arr, itemId, parent = null) => arr.reduce((acc, item) => {
  if (acc) {
    return acc;
  }

  if (item.uuid === itemId) {
    return [item, parent];
  }

  if (item.items) {
    return findFormItem(item.items, itemId, item);
  }

  return acc;
}, null);

export const findItemParent = (root, id, parent = null) => {
  if (root.uuid === id) { return parent; }

  if ('items' in root) {
    for (const i of root.items) {
      const r = findItemParent(i, id, root);
      if (r) { return r; }
    }
  }

  return null;
};

export const findHeader = items => items.filter(item => item.type === 'page' && item.params.header)[0] ?? null;
export const findQuestions = items => items.flatMap(item => item.type !== 'question' && item?.items ? findQuestions(item.items) : item);

const mapEntityTypeOrFolderToOptions = entityTypeOrFolders => entityTypeOrFolders?.map(entityTypeOrFolder => ({
  id: entityTypeOrFolder.id,
  label: entityTypeOrFolder.data.title
}));

const onlyUniqueFolders = entityFolders => _.uniqBy(entityFolders, 'id');

const getConditionValues = condition => Array.isArray(condition.value) ? condition.value : [condition.value];

/**
 * Recursively collect all element_id values from form items
 * @param {Array} items - Form items array
 * @returns {Set<string>} - Set of all element_id values
 */
const getAllElementIds = items => {
  const ids = new Set();
  const collectIds = itemList => {
    if (!itemList) {
      return;
    }
    for (const item of itemList) {
      if (item.element_id) {
        ids.add(item.element_id);
      }
      if (item.items) {
        collectIds(item.items);
      }
    }
  };
  collectIds(items);
  return ids;
};

/**
 * Generate a unique element_id for a new item
 * @param {string} type - Item type ('page', 'section', 'question')
 * @param {Set<string>} existingIds - Set of existing element_ids
 * @returns {string} - Unique element_id like 'page1', 'section2', etc.
 */
const generateElementId = (type, existingIds) => {
  const prefix = type === 'conditional_section' ? 'section' : type;
  let counter = 1;
  let elementId = `${prefix}${counter}`;
  while (existingIds.has(elementId)) {
    counter++;
    elementId = `${prefix}${counter}`;
  }
  return elementId;
};

export default {
  namespaced: true,

  state: { ...initialState() },

  getters: {
    checklist: state => state.checklist,
    checklistStatus: state => state.checklist.status,
    hasEntityItemInHeader: state => !!state.checklist.hasEntityItemInHeader,
    hasUserAutomatic: state => !!state.checklist.hasUserAutomatic,
    hasDatetimeAutomatic: state => !!state.checklist.hasDatetimeAutomatic,
    isCollapsible: state => state.isCollapsible,
    entityItems: state => state.entityItems,
    groups: state => state.groups,
    users: state => state.users,
    approvalUsers: state => state.approvalUsers,
    approvalActionUsers: state => state.approvalActionUsers,
    schedules: state => state.schedules,
    categories: state => state.categories,
    entityFolders: state => state.entityFolders,
    entityTypes: state => state.entityTypes,
    report_templates: state => state.report_templates,
    can_see_all_entity_items: state => state.can_see_all_entity_items,

    roles: state => state.roles,
    languages: state => state.languages,
    activeStage: state => state.activeStage,
    submitted: state => state.submitted,
    everybodyCanConduct: state => state.checklist.restrictions === null,
    everybodyCanEdit: state => state.checklist.editors === null,
    viewableBySubmitter: state => state.checklist.viewableBySubmitter,

    delete_item_modal: state => state.modals.delete_item,
    edit_rocc_modal: state => state.modals.edit_rocc_severity,
    edit_response_set_modal: state => state.modals.edit_response_set,
    isReorderPageModalOpen: state => state.modals.reorder_page.open,
    isIssueItemModalOpen: state => state.modals.automatic_issues.open,
    selectedIssueItem: state => state.modals.automatic_issues.item,
    isInspectionItemModalOpen: state => state.modals.automatic_inspections.open,
    selectedInspectionItem: state => state.modals.automatic_inspections.item,

    responseSetItemsInUse: state => getResponseSetItemsInUse(state.checklist.form),

    selectedItem: state => state.stages.build.selectedItem,
    stageSettingsSetting: state => state.stages.settings.setting,

    /* edit_response_set_modal_open: state => state.edit_response_set_modal_open,
    edited_response_set: state => state.edited_response_set,
    edited_response_set_origin_item: state => state.edited_response_set_origin_item, */

    find_item: state => item_uuid => findItem(state.checklist.form, item_uuid),

    edited_report_template: state => state.stages.report.editedReportTemplate,

    find_response_type: state => response_type => findResponseType(state.checklist.form, response_type),

    find_response_set: state => response_set_uuid => findResponseSet(state.checklist.form.params.response_sets, response_set_uuid),
    pages: state => state.checklist.form.items,
    isDragging: state => state.checklist.itemDragChoosed,

    isUpdating: state => state.isUpdating,
    isWordTemplateUploading: state => state.isWordTemplateUploading,

    lastPage: state => findLast(state.checklist.form.items, item => item.type === INSPECTION_ITEM_TYPE.PAGE),

    submitted_form_title_settings: state => state.checklist.form.submitted_form_title_settings,

    duplicateElementIds: state => {
      const ids = [];
      const collectIds = items => {
        if (!items) {
          return;
        }
        for (const item of items) {
          if (item.element_id) {
            ids.push(item.element_id);
          }
          if (item.items) {
            collectIds(item.items);
          }
        }
      };
      collectIds(state.checklist.form.items);
      const seen = new Set();
      const duplicates = new Set();
      for (const id of ids) {
        if (seen.has(id)) {
          duplicates.add(id);
        }
        seen.add(id);
      }
      return duplicates;
    },
  },

  actions: {
    addItem({
      commit,
      state,
    }, payload) {
      const { item } = payload;
      item.uuid = uuidv4();

      const responseSets = state.checklist.form.params.response_sets[0];

      if (isWordTemplateReportEnabled()) {
        const existingIds = getAllElementIds(state.checklist.form.items);

        if (['page', 'section', 'conditional_section', 'question'].includes(item.type)) {
          item.element_id = generateElementId(item.type, existingIds);
        }

        if (item.type !== 'question') {
          const nestedQuestion = defaultQuestion(responseSets ? responseSets.uuid : '');
          const updatedIds = new Set(existingIds);
          if (item.element_id) {
            updatedIds.add(item.element_id);
          }
          nestedQuestion.element_id = generateElementId('question', updatedIds);
          item.items = [nestedQuestion];
        }
      } else if (item.type !== 'question') {
        item.items = [defaultQuestion(responseSets ? responseSets.uuid : '')];
      }

      if (item.type === 'question') {
        item.required = false;
        item.response_type = 'list';
        item.notes_allowed = true;
        item.photos_allowed = true;
        item.issues_allowed = true;
        item.params = {
          ...defaultParams(
            item.response_type,
            responseSets ? responseSets.uuid : '',
          ),
        };
        item.check_conditions_for = [];
        item.categories = [];
        item.entity_types = [];
        item.entity_folders = [];
      }

      if (item.type === 'section') {
        item.required = false;
        item.weight = 1;
        item.repeat = false;
        item.$newSection = true;
      }

      if (item.type === 'conditional_section') {
        const dependsOn = findItem(state.checklist.form, payload.dependsOn);

        item.items = [];
        item.required = false;
        item.weight = 1;
        item.repeat = false;
        item.allConditionsMet = null;
        item.conditions = [{
          uuid: uuidv4(),
          item: payload.dependsOn,
          operator: defaultOperator(dependsOn.response_type),
          value: defaultValue(dependsOn.response_type, dependsOn, state.checklist.form.params.response_sets),
        }];

        dependsOn.check_conditions_for.unshift(item.uuid);
      }

      if (item.type === 'form_action') {
        item.items = [];
        item.required = false;
        item.weight = 1;
      }

      if ([INSPECTION_ITEM_TYPE.QUESTION, INSPECTION_ITEM_TYPE.SECTION].includes(item.type)) {
        commit('setBuildSelectedItem', item.uuid);
      }

      commit('insertItem', payload);

      return item.uuid;
    },

    saveResponseSet({
      commit,
      state,
    }, payload) {
      // generate uuids for responses that do not have one
      for (const i in payload.responseSet.responses) {
        if (typeof (payload.responseSet.responses[i].uuid) === 'undefined') {
          payload.responseSet.responses[i].uuid = uuidv4();
        }
      }

      // update response set if it was edited
      if (state.modals.edit_response_set.response_set) {
        state.modals.edit_response_set.response_set.responses = payload.responseSet.responses;
        state.modals.edit_response_set.response_set.title = payload.responseSet.title;
        state.modals.edit_response_set.response_set.type = payload.responseSet.type;

        // push response set if it was created
      } else {
        payload.responseSet.uuid = uuidv4();
        state.checklist.form.params.response_sets.push(payload.responseSet);
      }

      // update edited item's response set
      if (state.modals.edit_response_set.item) {
        if (state.modals.edit_response_set.item.response_type === 'list') {
          commit('setResponseSet', {
            item: state.modals.edit_response_set.item,
            response_set: payload.responseSet,
          });
        } else {
          commit('changeResponseType', {
            item: state.modals.edit_response_set.item,
            response_type: 'list',
            params: {
              response_set: payload.responseSet.uuid,
            },
          });
        }
      }
    },

    reorderPageModalOpened({ commit }, payload) {
      commit('reorderPageModalOpened', payload);
    },

    reorderPageModalClosed({ commit }, payload) {
      commit('reorderPageModalClosed', payload);
    },

    reorderPageModalSaved({ commit }, payload) {
      commit('reorderPageModalSaved', payload);
    },

    pageReordered({ commit }, payload) {
      commit('pageReordered', payload);
    },

    itemReordered({ commit }, payload) {
      commit('itemReordered', payload);
    },

    itemDragStarted({ commit }, payload) {
      commit('itemDragStarted', payload);
    },

    itemDragChoosed({ commit }) {
      commit('itemDragChoosed');
    },

    itemDragUnchoosed({ commit }) {
      commit('itemDragUnchoosed');
    },

    issueItemModalOpened({ commit }, payload) {
      commit('issueItemModalOpened', payload);
    },

    issueItemModalClosed({ commit }) {
      commit('issueItemModalClosed');
    },

    issueItemModalSaved({ commit }, payload) {
      if (payload.uuid) {
        commit('issueItemModalUpdated', payload);
      } else {
        commit('issueItemModalSaved', payload);
      }
    },

    inspectionItemModalOpened({ commit }, payload) {
      commit('inspectionItemModalOpened', payload);
    },

    inspectionItemModalClosed({ commit }) {
      commit('inspectionItemModalClosed');
    },

    inspectionItemModalSaved({ commit }, payload) {
      if (payload.uuid) {
        commit('inspectionItemModalUpdated', payload);
      } else {
        commit('inspectionItemModalSaved', payload);
      }
    },

    loadTemplate({ commit }, template) {
      if (!template.form.items[0]?.params?.header) {
        template.form.items.unshift(generateHeaderPage());
      }

      commit('loadChecklist', template);
    },

    setTranslationLanguages({ commit }, langs) {
      commit('setTranslationLanguages', langs);
    },

    beforeSave({ commit }) {
      commit('cleanupFormLevelRoccSettings');
    },
  },

  mutations: {
    setIsUpdating(state, isUpdating) {
      state.isUpdating = isUpdating;
    },

    setBuildSelectedItem(state, uuid) {
      Vue.set(state.stages.build, 'selectedItem', uuid);
    },

    setSchedules(state, value) {
      state.checklist.schedules = value;
    },

    updateAverageWeightedScoreCalculation(state, value) {
      averageWeightedScoreCalculation(state, value);
    },

    setFormRoccSettings(state, { roccEnabled, severities }) {
      if (roccEnabled !== undefined) {
        Vue.set(state.checklist.form.params, 'rocc_enabled', roccEnabled);
      }
      if (severities !== undefined) {
        Vue.set(state.checklist.form.params, 'severities', severities);
      }
    },

    cleanupFormLevelRoccSettings(state) {
      if (state.checklist.form.params.rocc_enabled) {
        const severities = state.checklist.form.params.severities || [];
        const validSeverities = severities.filter(s => s.category && s.threshold >= 1);

        if (validSeverities.length > 0) {
          Vue.set(state.checklist.form.params, 'severities', validSeverities);

          return;
        }
      }

      Vue.delete(state.checklist.form.params, 'rocc_enabled');
      Vue.delete(state.checklist.form.params, 'severities');
    },

    loadChecklist(state, checklist) {
      state.checklist = { ...state.checklist, ...checklist };

      state.checklist.hasUserAutomatic = Boolean(findResponseType(state.checklist.form, 'user_automatic'));
      state.checklist.hasDatetimeAutomatic = Boolean(findResponseType(state.checklist.form, 'datetime_automatic'));
      state.checklist.hasEntityItemInHeader = Boolean(findResponseType(state.checklist.form.items[0], 'entity_item'));

      this.commit('checklist_builder/updateIsCollapsible');
    },

    setTranslationLanguages(state, langs) {
      Vue.set(state.checklist, 'fullTranslationLanguages', langs);
    },

    updateIsCollapsible(state) {
      state.isCollapsible = isCollapsible(state.checklist.form);
    },

    loadEntityItems(state, entityItems) {
      Vue.set(state.checklist, 'entity_items', entityItems);
      state.entityItems = _.map(entityItems, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    loadGroups(state, groups) {
      state.groups = _.map(groups, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    loadUsers(state, users) {
      state.users = _.map(users, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    setUsers(state, users) {
      state.users = users.map(user => ({
        value: parseInt(user.id),
        label: user.name,
      }));
    },

    loadApprovalUsers(state, users) {
      state.approvalUsers = _.map(users, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    loadApprovalActionUsers(state, users) {
      state.approvalActionUsers = _.map(users, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    loadLanguages(state, languages) {
      state.languages = languages;
    },

    setApprovalActionUsers(state, users) {
      state.approvalActionUsers = users.map(user => ({
        value: parseInt(user.id),
        label: user.name,
      }));
    },

    setApprovalUsers(state, users) {
      state.approvalUsers = users.map(user => ({
        value: parseInt(user.id),
        label: user.name,
      }));
    },

    loadSchedules(state, schedules) {
      state.schedules = _.map(schedules, schedule => ({
        value: parseInt(schedule.id),
        label: schedule.title || `${t('schedules.no-title')}-${t(`schedules.repeat.${schedule.frequency}`)}`,
      }));
    },

    loadCategories(state, categories) {
      state.categories = _.map(categories, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    loadEntityTypes(state, entityTypes) {
      state.entityTypes = mapEntityTypeOrFolderToOptions(entityTypes);
    },

    appendEntityTypes(state, entityTypes) {
      state.entityTypes = [
        ...state.entityTypes,
        ...mapEntityTypeOrFolderToOptions(entityTypes)
      ];
    },

    loadEntityFolders(state, entityFolders) {
      state.entityFolders = onlyUniqueFolders(mapEntityTypeOrFolderToOptions(entityFolders));
    },

    appendEntityFolders(state, entityFolders) {
      state.entityFolders = onlyUniqueFolders([
        ...state.entityFolders,
        ...mapEntityTypeOrFolderToOptions(entityFolders)
      ]);
    },

    loadReportTemplates(state, reportTemplates) {
      state.report_templates = reportTemplates;
    },

    loadRoles(state, roles) {
      state.roles = _.map(roles, (value, key) => ({
        value: parseInt(key),
        label: value,
      }));
    },

    updateSubmittedState(state, submittedState) {
      state.submitted = submittedState;
    },

    addChecklistState(state, status) {
      state.checklist.status = status;
    },

    resetBuilder(state) {
      Object.assign(state, initialState());
    },

    reset(state) {
      Object.assign(state, initialState());
    },

    insertItem(state, payload) {
      const { items } = findItem(state.checklist.form, payload.parent);
      // prepend item
      if (payload.prepend) {
        items.splice(0, 0, payload.item);

        // insert after another item
      } else if (payload.after) {
        for (const i in items) {
          if (items[i].uuid === payload.after) {
            items.splice(parseInt(i) + 1, 0, payload.item);
            break;
          }
        }
        // append item
      } else {
        items.push(payload.item);
      }

      Vue.set(state.stages.build, 'selectedItem', payload.item.uuid);
      this.commit('checklist_builder/updateIsCollapsible');
    },

    changeCondition(state, payload) {
      const { condition } = payload;

      // try to keep existing parameter
      condition.operator = payload.operator.value;
      if (payload.operator.parameter) {
        if (condition.value === null) {
          condition.value = defaultValue(payload.dependsOnItem.response_type, payload.dependsOnItem, state.checklist.form.params.response_sets);
        }
        if (condition.value === null) {
          if (payload.operator.multiple) {
            condition.value = [];
          }
        }
        const current_value_is_array = condition.value && condition.value.constructor === Array;
        if (payload.operator.multiple && !current_value_is_array) {
          if (condition.value === null || condition.value === undefined) {
            condition.value = defaultValue(
              payload.dependsOnItem.response_type,
              payload.dependsOnItem, state.checklist.form.params.response_sets
            );
          }

          condition.value = [condition.value];
        } else if (!payload.operator.multiple && current_value_is_array) {
          condition.value = condition.value[0];
        } else {
          // in this case, there is no need to update the condition value
        }
      } else {
        condition.value = null;
      }

      if (payload.operator.value === INSPECTION_LIST_CONDITIONS.EMPTY) {
        payload.item.allConditionsMet = true;
      }
    },

    changeResponseType(state, payload) {
      const item = findItem(state.checklist.form, payload.item.uuid);
      const previousResponseType = item.response_type;

      headerQuestionChanges(state, item.response_type, payload.response_type);

      item.response_type = payload.response_type;

      if (
        [ResponsesTypes.instruction, ResponsesTypes.userAutomatic, ResponsesTypes.dateTimeAutomatic]
          .includes(item.response_type)) {
        item.required = false;
      }

      if (item.response_type === ResponsesTypes.entityItem) {
        item.entity_folders = item.entity_folders || [];
        item.entity_types = item.entity_types || [];
      }

      if (typeof (payload.params) === 'undefined') {
        payload.params = {};
      }

      const responseSets = state.checklist.form.params.response_sets[0];
      item.params = {
        ...defaultParams(
          item.response_type,
          responseSets ? responseSets.uuid : '',
          item.params.rte_title,
        ),
        ...payload.params,
      };

      // delete all conditionals sections that depend on this value
      if (item.check_conditions_for && item.check_conditions_for.length) {
        // for (var i in item.check_conditions_for) {

        for (let i = item.check_conditions_for.length - 1; i >= 0; i--) {
          deleteItem(state.checklist.form, item.check_conditions_for[i]);
        }
      }

      if (item.response_type === ResponsesTypes.geoLocation) {
        item.issues_allowed = false;
      } else if (previousResponseType === ResponsesTypes.geoLocation) {
        item.issues_allowed = true;
      }

      //item.issues_allowed = item.response_type === ResponsesTypes.geoLocation ? false : true;
      state.checklist.hasEntityItemInHeader = findResponseType(state.checklist.form.items[0], 'entity_item');
    },

    setResponseSet(state, payload) {
      payload.item.params.response_set = payload.response_set.uuid;

      if (payload.item.check_conditions_for && payload.item.check_conditions_for.length) {
        for (let i = payload.item.check_conditions_for.length - 1; i >= 0; i--) {
          const item = findItem(state.checklist.form, payload.item.check_conditions_for[i]);
          const response_set = findResponseSet(
            state.checklist.form.params.response_sets,
            payload.item.params.response_set
          );
          const isConditionValueInResponses = response_set.responses.some(
            response => getConditionValues(item.conditions[0])?.includes(response.uuid)
          );

          if (!isConditionValueInResponses) {
            if (isLogicMultipleResponse(item.conditions[0])) {
              item.conditions[0].value = [];
            } else {
              item.conditions[0].value = undefined;
            }
          }
        }
      }
    },

    openRoccSeverityModal(state, payload) {
      state.modals.edit_rocc_severity.item = payload.item;
      state.modals.edit_rocc_severity.open = true;
    },

    closeRoccModal(state) {
      state.modals.edit_rocc_severity.open = false;
      state.modals.edit_rocc_severity.item = null;
    },

    openDeleteModal(state, payload) {
      state.modals.delete_item.item = payload.item;
      state.modals.delete_item.parent = typeof payload.parent === 'string'
        ? findItem(state.checklist.form, payload.parent)
        : payload.parent;
      state.modals.delete_item.open = true;
    },

    closeDeleteModal(state) {
      state.modals.delete_item.open = false;
      state.modals.delete_item.item = null;
      state.modals.delete_item.parent = null;
    },

    saveRoccSeverity(state, data) {
      Vue.set(state.modals.edit_rocc_severity.item.params, 'rocc_enabled', data.roccEnabled);
      Vue.set(state.modals.edit_rocc_severity.item.params, 'severities', data.severities);
      this.commit('checklist_builder/closeRoccModal');
    },

    deleteItem(state) {
      const { item } = state.modals.delete_item;

      headerQuestionDeletion(state, item);

      if (state.stages.build.selectedItem && state.stages.build.selectedItem === item.uuid) {
        Vue.set(state.stages.build, 'selectedItem', null);
      }

      // delete conditional sections of this item
      if (item.check_conditions_for && item.check_conditions_for.length) {
        for (let i = item.check_conditions_for.length - 1; i >= 0; i--) {
          deleteItem(state.checklist.form, item.check_conditions_for[i]);
        }
      }

      // delete item itself
      deleteItem(state.checklist.form, item.uuid);

      state.modals.delete_item.open = false;
      state.modals.delete_item.item = null;
      state.modals.delete_item.parent = null;
      state.checklist.hasEntityItemInHeader = findResponseType(state.checklist.form.items[0], 'entity_item');

      this.commit('checklist_builder/updateIsCollapsible');
    },

    cloneItem(_state, payload) {
      const itemParent = typeof payload.parent === 'string'
        ? findItem(_state.checklist.form, payload.parent)
        : payload.parent;

      function cloneConditions(newUuid, item, items, conditionsList = []) {
        const uuids = items.map(item => item.uuid);
        conditionsList.sort((a, b) => {
          return uuids.indexOf(a) - uuids.indexOf(b);
        });
        conditionsList = conditionsList.filter(uuid => items.find(item => item.uuid === uuid));

        const sections = conditionsList.map(uuid => {
          const section = items.find(item => item.uuid === uuid);

          return R.mergeDeepRight(section, {
            uuid: uuidv4(),
            node_id: null,
            items: updateChildrenUuids(section.items),
            conditions: section.conditions.map(condition => ({
              ...condition,
              item: newUuid,
            })),
          });
        });

        return {
          sections,
          uuids: sections.map(({ uuid }) => uuid),
        };
      }

      function updateChildrenUuids(items) {
        const eachItemUuidUpdated = items
          .filter(({ type }) => type !== INSPECTION_ITEM_TYPE.CONDITIONAL)
          .map(({ uuid, node_id, items, ...rest }) => ({
            uuid: uuidv4(),
            node_id: null,
            ...rest,
            params: {
              ...rest.params,
              node_id: null,
            },
            ...(items && { items: updateChildrenUuids(items) }),
          }));

        return eachItemUuidUpdated
          .reduce((accumulator, item) => {
            const {
              uuids,
              sections,
            } = cloneConditions(item.uuid, item, items, item.check_conditions_for);
            return [
              ...accumulator,
              {
                ...item,
                check_conditions_for: uuids,
              },
              ...(sections || []),
            ];
          }, []);
      }

      const cloneFn = (parentItems, item) => {
        const uuid = uuidv4();
        const conditions = item?.check_conditions_for?.length > 0
          ? cloneConditions(uuid, item, parentItems, item.check_conditions_for)
          : undefined;
        const newClonedItem = {
          ...item,
          node_id: null,
          params: {
            ...item.params,
            node_id: null,
          },
          uuid,
          ...(item.items && { items: updateChildrenUuids(item.items) }),
          ...(conditions && { check_conditions_for: [...conditions.uuids] }),
        };

        return [newClonedItem, ...(conditions?.sections || [])].filter(i => i);
      };

      function insertClonedToParent(cloned, payload) {
        const index = itemParent.items.findIndex(({ uuid }) => uuid === payload.item.uuid);
        const indexToAdd = index + cloned.length;
        return R.insertAll(indexToAdd, cloned, itemParent.items);
      }
      const cloned = cloneFn(itemParent.items, payload.item);

      const newItems = insertClonedToParent(cloned, payload);
      Vue.set(itemParent, 'items', newItems);
      this.commit('checklist_builder/updateIsCollapsible');
    },

    openSettingsSetting(state, { active }) {
      state.stages.settings.setting.active = active;
    },

    reorderPageModalOpened({ modals }) {
      Vue.set(modals.reorder_page, 'open', true);
    },

    reorderPageModalClosed({ modals }) {
      Vue.set(modals.reorder_page, 'open', false);
    },

    reorderPageModalSaved(state, payload) {
      const pages = [...state.checklist.form.items];
      const sortedPages = pages.sort((current, next) => payload.indexOf(current) - payload.indexOf(next));

      Vue.set(state.modals.reorder_page, 'open', false);
      Vue.set(state.checklist.form, 'items', sortedPages);
    },

    pageReordered(state, payload) {
      Vue.set(state.checklist.form, 'items', payload);
    },

    itemReordered(state, {
      destination,
      items,
    }) {
      const { currentDraggingItem } = state.checklist;

      if (!currentDraggingItem) {
        return;
      }

      const dragsEntityItemQuestion
        = currentDraggingItem.item.response_type === ResponsesTypes.entityItem
        || (currentDraggingItem.item?.items && findResponseType(currentDraggingItem.item, ResponsesTypes.entityItem));

      if (dragsEntityItemQuestion) {
        const header = state.checklist.form.items[0];
        const headerEntityItemQuestion = findResponseType(header, ResponsesTypes.entityItem);
        const headerHasEntityItemQuestion = headerEntityItemQuestion
          && headerEntityItemQuestion.uuid !== currentDraggingItem.item.uuid;
        const rootDestinationContainer = findRootItem(state.checklist.form, destination.uuid, 'page');

        if (headerHasEntityItemQuestion && dragsEntityItemQuestion && rootDestinationContainer.params.header) {
          Vue.set(state.checklist, 'currentDraggingItem', null);

          return;
        }

        if (
          rootDestinationContainer.params.header
          && (
            InspectionEntityItemsHelper.isInsideRepeatedSection(state.checklist.form, destination)
            || rootDestinationContainer.params.repeat
          )
        ) {
          Vue.set(state.checklist, 'currentDraggingItem', null);

          return;
        }
      }

      const checklist = state.checklist.form.items;

      const [, originContainer] = findFormItem(checklist, currentDraggingItem.item.uuid);
      const originItems = R.difference(
        originContainer.items,
        [currentDraggingItem.item, ...currentDraggingItem.relatedConditions]
      );

      const [destinationContainer] = findFormItem(checklist, destination.uuid);
      const destinationItems = R.pipe(
        R.differenceWith,
        newItems => R.insertAll(
          (newItems.findIndex(item => item.uuid === currentDraggingItem.item.uuid) + 1),
          currentDraggingItem.relatedConditions,
          newItems,
        ),
      )(R.identical, items, currentDraggingItem.relatedConditions);

      Vue.set(originContainer, 'items', originItems);
      Vue.set(destinationContainer, 'items', destinationItems);
      Vue.set(state.checklist, 'currentDraggingItem', null);
      trackEvent(EventTypes.dragAndDrop, EVENTS.DRAG_AND_DROP, { label: currentDraggingItem.item.title });

      state.checklist.hasEntityItemInHeader = findResponseType(state.checklist.form.items[0], 'entity_item');
    },

    itemDragStarted(state, payload) {
      const [currentItem, parent] = findFormItem(state.checklist.form.items, payload.uuid);
      const relatedConditions = currentItem.check_conditions_for
        ? parent.items.filter(({ uuid }) => currentItem.check_conditions_for.includes(uuid))
        : [];

      Vue.set(state.checklist, 'currentDraggingItem', {
        item: currentItem,
        relatedConditions,
        parent,
      });
    },

    itemDragChoosed(state) {
      Vue.set(state.checklist, 'itemDragChoosed', true);
    },

    itemDragUnchoosed(state) {
      Vue.set(state.checklist, 'itemDragChoosed', false);
    },

    editResponseSet(state, payload) {
      state.modals.edit_response_set.response_set = payload.response_set;
      state.modals.edit_response_set.item = payload.item;
      state.modals.edit_response_set.type = payload.response_set.type;
      state.modals.edit_response_set.open = true;
      state.modals.edit_response_set.isTranslationMode = payload.isTranslationMode;
    },

    createResponseSet(state, payload) {
      state.modals.edit_response_set.item = payload.item;
      state.modals.edit_response_set.type = payload.type;
      state.modals.edit_response_set.open = true;
    },

    deleteResponseSet(state, payload) {
      if (responseSetIsInUse(state.checklist.form, payload.response_set.uuid)) {
        eventBus.$emit('error', t('This response set is in use, it can not be deleted!'));
      } else {
        deleteResponseSet(state.checklist.form.params.response_sets, payload.response_set.uuid);
      }
    },

    closeResponseSetEditorModal(state) {
      state.modals.edit_response_set.response_set = null;
      state.modals.edit_response_set.item = null;
      state.modals.edit_response_set.open = false;
      state.modals.edit_response_set.isTranslationMode = false;
    },

    editReportTemplate(state, reportTemplate) {
      state.stages.report.editedReportTemplate = reportTemplate;
    },

    activeStage(state, payload) {
      state.activeStage = payload;
    },

    everybodyCanConduct(state, payload) {
      if (payload === true) {
        state.checklistPreviousState.restrictions = state.checklist.restrictions;
      }

      state.checklist.restrictions = payload === true
        ? null
        : state.checklistPreviousState.restrictions;
    },

    everybodyCanViewReport(state, payload) {
      if (payload === true) {
        state.checklistPreviousState.reportRestrictions = state.checklist.reportRestrictions;
      }

      state.checklist.reportRestrictions = payload === true
        ? null
        : state.checklistPreviousState.reportRestrictions;
    },

    setViewableBySubmitter(state, payload) {
      state.checklist.viewableBySubmitter = payload;
    },

    everybodyCanEdit(state, payload) {
      if (payload === true) {
        state.checklistPreviousState.editors = state.checklist.editors;
      }

      state.checklist.editors = payload === true
        ? null
        : state.checklistPreviousState.editors;
    },

    setChecklistRestrictions(state, payload) {
      state.checklist.restrictions = payload;
    },

    setReportRestrictions(state, payload) {
      state.checklist.reportRestrictions = payload;
    },

    setEditors(state, payload) {
      state.checklist.editors = payload;
    },

    setEditableByAll(state, payload) {
      state.checklist.editableByAll = payload;
    },

    setApprovers(state, payload) {
      state.checklist.approvers = payload;
    },

    setApprovalsEnabled(state, payload) {
      state.checklist.approvalsEnabled = payload;
    },

    setApprovalsEntitySpecificAssignments(state, payload) {
      state.checklist.approvalsEntitySpecificAssignments = payload;
    },

    setBlurFaces(state, payload) {
      state.checklist.blurFaces = payload;
    },

    setImproveWriting(state, payload) {
      state.checklist.improveWriting = payload;
    },

    setFillableByAll(state, payload) {
      state.checklist.fillableByAll = payload;
    },

    setViewableByAll(state, payload) {
      state.checklist.viewableByAll = payload;
    },

    setCanSeeAllEntityItems(state, payload) {
      state.can_see_all_entity_items = payload;
    },

    loadDefaultForm(state) {
      state.checklist.form = getDefaultForm();
      // Vue.set(state.checklist, "form", Object.assign({}, state.checklist.form, getDefaultForm()));
    },

    issueItemModalOpened(state, payload) {
      Vue.set(state.modals, 'automatic_issues', R.mergeDeepRight(state.modals.automatic_issues, {
        open: true,
        item: payload.item || null,
        parent: payload.parent,
        after: payload.after || null,
        prepend: payload.prepend || null,
      }));
    },

    issueItemModalClosed(state) {
      Vue.set(state.modals, 'automatic_issues', R.mergeDeepRight(state.modals.automatic_issues, {
        open: false,
        item: null,
        parent: null,
        after: null,
        prepend: null,
      }));
    },

    issueItemModalSaved(state, payload) {
      const issueItem = encoders.issueItem(payload);
      const {
        parent,
        after,
      } = state.modals.automatic_issues;

      if (state.modals.automatic_issues.prepend) {
        Vue.set(parent, 'items', R.prepend(issueItem, parent.items));
      } else if (after) {
        const index = parent.items.findIndex(item => item.uuid === after.uuid) + 1;
        Vue.set(parent, 'items', R.insert(index, issueItem, parent.items));
      } else {
        Vue.set(parent, 'items', R.append(issueItem, parent.items));
      }

      Vue.set(state.modals.automatic_issues, 'parent', null);
      Vue.set(state.modals.automatic_issues, 'after', null);
      Vue.set(state.modals.automatic_issues, 'prepend', false);
      Vue.set(state.modals.automatic_issues, 'open', false);
    },

    issueItemModalUpdated(state, payload) {
      const issueItem = encoders.issueItem(payload);
      const { parent } = state.modals.automatic_issues;

      const issueToUpdateIndex = parent.items.findIndex(item => item.uuid === issueItem.uuid);

      if (issueToUpdateIndex >= 0) {
        Vue.set(parent.items, issueToUpdateIndex, issueItem);
      }

      Vue.set(state.modals.automatic_issues, 'parent', null);
      Vue.set(state.modals.automatic_issues, 'after', null);
      Vue.set(state.modals.automatic_issues, 'prepend', false);
      Vue.set(state.modals.automatic_issues, 'open', false);
    },

    inspectionItemModalOpened(state, payload) {
      Vue.set(state.modals, 'automatic_inspections', R.mergeDeepRight(state.modals.automatic_inspections, {
        open: true,
        item: payload.item || null,
        parent: payload.parent,
        after: payload.after || null,
        prepend: payload.prepend || null,
      }));
    },

    inspectionItemModalClosed(state) {
      Vue.set(state.modals, 'automatic_inspections', R.mergeDeepRight(state.modals.automatic_inspections, {
        open: false,
        item: null,
        parent: null,
        after: null,
        prepend: null,
      }));
    },

    inspectionItemModalSaved(state, payload) {
      const inspectionActionItem = encoders.inspectionActionItem(payload);
      const {
        parent,
        after,
      } = state.modals.automatic_inspections;

      if (state.modals.automatic_inspections.prepend) {
        Vue.set(parent, 'items', R.prepend(inspectionActionItem, parent.items));
      } else if (after) {
        const index = parent.items.findIndex(item => item.uuid === after.uuid) + 1;
        Vue.set(parent, 'items', R.insert(index, inspectionActionItem, parent.items));
      } else {
        Vue.set(parent, 'items', R.append(inspectionActionItem, parent.items));
      }

      Vue.set(state.modals.automatic_inspections, 'parent', null);
      Vue.set(state.modals.automatic_inspections, 'after', null);
      Vue.set(state.modals.automatic_inspections, 'prepend', false);
      Vue.set(state.modals.automatic_inspections, 'open', false);
    },

    inspectionItemModalUpdated(state, payload) {
      const inspectionActionItem = encoders.inspectionActionItem(payload);
      const { parent } = state.modals.automatic_inspections;

      const inspectionToUpdateIndex = parent.items.findIndex(item => item.uuid === inspectionActionItem.uuid);

      if (inspectionToUpdateIndex >= 0) {
        Vue.set(parent.items, inspectionToUpdateIndex, inspectionActionItem);
      }

      Vue.set(state.modals.automatic_inspections, 'parent', null);
      Vue.set(state.modals.automatic_inspections, 'after', null);
      Vue.set(state.modals.automatic_inspections, 'prepend', false);
      Vue.set(state.modals.automatic_inspections, 'open', false);
    },

    setSubmittedFormTitleSettings(state, settings) {
      state.checklist.form.submitted_form_title_settings = settings;
    },

    setTranslationsEnabled(state, value) {
      state.checklist.translationsEnabled = value;
    },

    setWordTemplateUploading(state, value) {
      state.isWordTemplateUploading = value;
    },
  },
};

function isLogicMultipleResponse({ operator }) {
  const multiple = [
    INSPECTION_LIST_CONDITIONS.INCLUDES,
    INSPECTION_LIST_CONDITIONS.ONE_OF,
    INSPECTION_LIST_CONDITIONS.NOT_ONE_OF,
  ];
  return multiple.includes(operator);
}

function isCollapsible(form) {
  return hasCollapsibleTemplateBuilder.value && countNodesUntilThreshold(form, CHECKLIST_COLLAPSE_THRESHOLD) >= CHECKLIST_COLLAPSE_THRESHOLD;
}

/**
 * Recursively count items in the tree until the counter reaches the "threshold"
 *
 * @param {object} item
 * @param {number} threshold
 * @returns {number}
 */
function countNodesUntilThreshold(item, threshold) {
  let count = 0;

  if (item.items) {
    count += item.items.length;

    if (count >= threshold) {
      return count;
    }

    for (const subItem of item.items) {
      count += countNodesUntilThreshold(subItem, threshold - count);
      if (count >= threshold) {
        return count;
      }
    }
  }

  return count;
}

export function getDefaultForm() {
  const defaultResponseSetUUID = uuidv4();
  const defaultQ = defaultQuestion(defaultResponseSetUUID);
  const featureEnabled = isWordTemplateReportEnabled();

  if (featureEnabled) {
    defaultQ.element_id = 'question4';
  }

  return {
    uuid: uuidv4(),
    type: 'form',
    items: [
      generateHeaderPage(),
      {
        uuid: uuidv4(),
        ...(featureEnabled && { element_id: 'page2' }),
        type: 'page',
        title: '',
        items: [defaultQ],
        params: {
          collapsed: false,
        },
      },
    ],
    params: {
      format_version: 2,
      response_sets: [
        {
          uuid: defaultResponseSetUUID,
          responses: [
            {
              uuid: uuidv4(),
              title: t('Yes'),
              color: '#4AD862',
              score: 1,
              negative: false,
            },
            {
              uuid: uuidv4(),
              title: t('No'),
              color: '#FF6E44',
              score: 0,
              negative: true,
            },
            {
              uuid: uuidv4(),
              title: t('N/A'),
              color: '#777777',
              score: null,
              negative: false,
            },
          ],
        },
        {
          uuid: uuidv4(),
          responses: [
            {
              uuid: uuidv4(),
              title: t('Intact'),
              color: '#4AD862',
              score: 1,
              negative: false,
            },
            {
              uuid: uuidv4(),
              title: t('Defect'),
              color: '#FF6E44',
              score: 0,
              negative: true,
            },
            {
              uuid: uuidv4(),
              title: t('N/A'),
              color: '#777777',
              score: null,
              negative: false,
            },
          ],
        },
        {
          uuid: uuidv4(),
          responses: [
            {
              uuid: uuidv4(),
              title: t('Good'),
              color: '#4AD862',
              score: 2,
              negative: false,
            },
            {
              uuid: uuidv4(),
              title: t('Medium'),
              color: '#FDAC43',
              score: 1,
              negative: false,
            },
            {
              uuid: uuidv4(),
              title: t('Bad'),
              color: '#FF6E44',
              score: 0,
              negative: true,
            },
            {
              uuid: uuidv4(),
              title: t('N/A'),
              color: '#777777',
              score: null,
              negative: false,
            },
          ],
        },
      ],
    },
    submitted_form_title_settings: getDefaultTitleSettings()
  };
}

function findResponseSet(response_sets, uuid) {
  for (const i in response_sets) {
    if (response_sets[i].uuid === uuid) {
      return response_sets[i];
    }
  }
  return null;
}

function getResponseSetItemsInUse(item, responseSetItems = []) {
  if (item.conditions) {
    for (const condition of item.conditions) {
      responseSetItems.push(...(getConditionValues(condition)));
    }
  }

  if (item.items) {
    for (const childItem of item.items) {
      getResponseSetItemsInUse(childItem, responseSetItems);
    }
  }

  return responseSetItems;
}

function responseSetIsInUse(item, response_set_uuid) {
  if (item.params && item.params.response_set === response_set_uuid) {
    return true;
  }

  if (item.items) {
    for (const i in item.items) {
      const res = responseSetIsInUse(item.items[i], response_set_uuid);
      if (res !== false) {
        return res;
      }
    }
  }

  return false;
}

function defaultQuestion(defaultResponseSetUUID) {
  return {
    uuid: uuidv4(),
    title: '',
    type: 'question',
    response_type: 'list',
    required: false,
    notes_allowed: true,
    photos_allowed: true,
    issues_allowed: true,
    params: { ...defaultParams('list', defaultResponseSetUUID) },
    check_conditions_for: [],
    categories: [],
    entity_folders: [],
    entity_types: [],
    add_to_submitted_form_title: false,
  };
}

function defaultOperator(response_type) {
  switch (response_type) {
    case INSPECTION_ITEM_RESPONSE.QRCODE:
    case INSPECTION_ITEM_RESPONSE.LIST:
      return 'is';
    case INSPECTION_ITEM_RESPONSE.NUMBER:
      return '=';
    case INSPECTION_ITEM_RESPONSE.CHECKBOX:
      return 'is_true';
  }
}

function defaultValue(response_type, item, response_sets) {
  switch (response_type) {
    case 'list': {
      const response_set = findResponseSet(response_sets, item.params.response_set);

      if (response_set.responses[0]) {
        return response_set.responses[0].uuid;
      }

      return null;
    }

    case INSPECTION_ITEM_RESPONSE.NUMBER:
    case INSPECTION_ITEM_RESPONSE.CHECKBOX:
    case INSPECTION_ITEM_RESPONSE.QRCODE:
    case INSPECTION_ITEM_RESPONSE.ENTITY_ITEM:
      return null;
  }
}

function defaultParams(response_type, defaultResponseSetUUID, rteTitle = undefined) {
  const paramsByType = {
    [ResponsesTypes.userAutomatic]: {
      automatic: true,
    },
    [ResponsesTypes.dateTimeAutomatic]: {
      automatic: true,
    },
    [ResponsesTypes.list]: {
      response_set: defaultResponseSetUUID,
      multiple_selection: false,
    },
    [ResponsesTypes.datetime]: {
      date: true,
      time: true,
    },
    [ResponsesTypes.photo]: {
      allow_multiple: true,
      allow_drawing: true,
      allow_gallery_selection: true,
    },
    [ResponsesTypes.instruction]: {
      image: null,
    },
    [ResponsesTypes.annotation]: {
      image: null,
    },
    [ResponsesTypes.entityItem]: {
      multiple_selection: false
    },
    [ResponsesTypes.text]: {
      show_rte_response: false,
    }
  };

  const result = paramsByType[response_type] || {};

  if (rteTitle !== undefined) {
    result.rte_title = rteTitle;
  }

  return result;
}

function findItem(item, uuid) {
  if (item.uuid === uuid) {
    return item;
  }
  if (item.items) {
    for (const i in item.items) {
      const res = findItem(item.items[i], uuid);
      if (res !== false) {
        return res;
      }
    }
  }
  return false;
}

// const findResponseType = find('response_type')
// findResponseType(item, 'user_automatic')

export function findResponseType(item, response_type) {
  if (item.response_type === response_type) {
    return item;
  }

  if (item.items) {
    for (const i in item.items) {
      const res = findResponseType(item.items[i], response_type);
      if (res !== false) {
        return res;
      }
    }
  }

  return false;
}

function deleteResponseSet(response_sets, uuid) {
  if (response_sets.length) {
    for (const i in response_sets) {
      if (response_sets[i].uuid === uuid) {
        response_sets.splice(i, 1);
      }
    }
  }
}

function headerQuestionChanges(state, itemResponseType, payloadResponseType) {
  if (itemResponseType === 'user_automatic' && itemResponseType !== payloadResponseType) {
    state.checklist.hasUserAutomatic = false;
  }

  if (itemResponseType === 'datetime_automatic' && itemResponseType !== payloadResponseType) {
    state.checklist.hasDatetimeAutomatic = false;
  }

  if (payloadResponseType === 'user_automatic') {
    state.checklist.hasUserAutomatic = true;
  }

  if (payloadResponseType === 'datetime_automatic') {
    state.checklist.hasDatetimeAutomatic = true;
  }
}

function headerQuestionDeletion(state, item) {
  if (item.response_type === 'user_automatic') {
    state.checklist.hasUserAutomatic = false;
  }

  if (item.response_type === 'datetime_automatic') {
    state.checklist.hasDatetimeAutomatic = false;
  }
}

function averageWeightedScoreCalculation(state, value) {
  state.checklist.form.params.average_weighted_score_calculation = value;
}
